import React, { useCallback, useEffect, useRef, useState } from "react";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import SummaryPopover, {
    copyTextToClipboard,
    SummaryPopoverSection,
    SummaryPopoverValue,
} from "./SummaryPopover";

const COPY_FEEDBACK_MS = 1400;

const getFunctionCode = (fn) => (typeof fn === "string" ? fn : fn?.fn_name);

const getFunctionLabel = (code, allFunctions) =>
    (Array.isArray(allFunctions) &&
        allFunctions.find((f) => f.value === code)?.label) ||
    code;

const getFunctionParams = (code, functionValues) => {
    const params = {};
    if (code === "max_len" && functionValues?.maxLen) {
        params.length = functionValues.maxLen;
    }
    if (code === "pad_with") {
        if (functionValues?.padWithCharacter) {
            params.character = functionValues.padWithCharacter;
        }
        if (functionValues?.padWithLength) {
            params.length = functionValues.padWithLength;
        }
    }
    if (code === "left" && functionValues?.left) {
        params.from_left = functionValues.left;
    }
    if (code === "right" && functionValues?.right) {
        params.from_right = functionValues.right;
    }
    return params;
};

const describeFunction = (fn, allFunctions, functionValues) => {
    const code = getFunctionCode(fn);
    const label = getFunctionLabel(code, allFunctions);
    const params = getFunctionParams(code, functionValues);

    const details = Object.entries(params).map(
        ([key, value]) => `${key.replace(/_/g, " ")}: ${value}`,
    );

    return details.length > 0 ? `${label} (${details.join(", ")})` : label;
};

/**
 * Whole-row snapshot of the configured options. One JSON payload beats copying
 * each function, comparison and default value out of the card one at a time.
 */
const buildOptionsJson = ({
    sourceKey,
    functions,
    defaultValue,
    prefix,
    comparisons,
    allFunctions,
    functionValues,
}) => {
    const payload = {
        source_field: sourceKey ?? null,
        functions: functions.map((fn) => {
            const code = getFunctionCode(fn);
            const params = getFunctionParams(code, functionValues);
            const entry = {
                name: code ?? null,
                label: getFunctionLabel(code, allFunctions) ?? null,
            };
            if (Object.keys(params).length > 0) entry.params = params;
            return entry;
        }),
        default_value:
            defaultValue === undefined || defaultValue === ""
                ? null
                : defaultValue,
        prefix_label: prefix === undefined || prefix === "" ? null : prefix,
        comparisons: comparisons.map((row) => ({
            compare_to: row?.compare_to ?? null,
            map_to: row?.map_to ?? null,
        })),
    };

    return JSON.stringify(payload, null, 2);
};

const CopyOptionsJsonButton = ({ getJson, label }) => {
    const [copied, setCopied] = useState(false);
    const resetTimer = useRef(null);

    useEffect(
        () => () => {
            if (resetTimer.current) clearTimeout(resetTimer.current);
        },
        [],
    );

    const handleCopy = useCallback(async () => {
        if (!(await copyTextToClipboard(getJson()))) return;
        setCopied(true);
        if (resetTimer.current) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(
            () => setCopied(false),
            COPY_FEEDBACK_MS,
        );
    }, [getJson]);

    return (
        <Button
            size="small"
            onClick={handleCopy}
            startIcon={
                copied ? (
                    <CheckRoundedIcon sx={{ fontSize: 14 }} />
                ) : (
                    <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
                )
            }
            sx={{
                minWidth: 0,
                padding: "2px 8px",
                fontSize: "11px",
                fontWeight: 700,
                lineHeight: 1.4,
                textTransform: "none",
                fontFamily: '"Nunito", sans-serif',
                color: copied ? "#2e7d32" : "#1c1c1e",
                backgroundColor: "#f2f2f4",
                borderRadius: "6px",
                "& .MuiButton-startIcon": { marginRight: "4px" },
                "&:hover": { backgroundColor: "#e5e5ea" },
            }}
        >
            {copied ? "Copied" : (label ?? "Copy JSON")}
        </Button>
    );
};

/**
 * Read-only hover summary of a mapping row's configured options, styled to
 * match the trackers flow-diagram grouped-source detail popover.
 */
const FieldOptionsSummaryPopover = ({
    open,
    anchorEl,
    item,
    allFunctions,
    functionValues,
    language,
    onClose,
}) => {
    const functions = item?.options?.functions || [];
    const defaultValue = item?.options?.default;
    const prefix = item?.options?.prefix;
    const comparisons = Array.isArray(item?.compared_fields?.[item?.sourceKey])
        ? item.compared_fields[item.sourceKey].filter(
              (row) => row?.compare_to || row?.map_to,
          )
        : [];

    const hasFunctions = functions.length > 0;
    const hasDefault = defaultValue !== null && defaultValue !== "";
    const hasPrefix = prefix !== null && prefix !== "";
    const hasComparisons = comparisons.length > 0;
    const hasAnything =
        hasFunctions || hasDefault || hasPrefix || hasComparisons;

    const getJson = useCallback(
        () =>
            buildOptionsJson({
                sourceKey: item?.sourceKey,
                functions,
                defaultValue: hasDefault ? defaultValue : undefined,
                prefix: hasPrefix ? prefix : undefined,
                comparisons,
                allFunctions,
                functionValues,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            item?.sourceKey,
            functions,
            defaultValue,
            hasDefault,
            prefix,
            hasPrefix,
            comparisons,
            allFunctions,
            functionValues,
        ],
    );

    const sections = [];
    if (hasFunctions) {
        sections.push(
            <SummaryPopoverSection
                key="functions"
                label={language?.lead?.functions ?? "Functions"}
            >
                {functions.map((fn, fnIndex) => (
                    <SummaryPopoverValue
                        key={`${getFunctionCode(fn)}-${fnIndex}`}
                    >
                        {describeFunction(fn, allFunctions, functionValues)}
                    </SummaryPopoverValue>
                ))}
            </SummaryPopoverSection>,
        );
    }
    if (hasDefault) {
        sections.push(
            <SummaryPopoverSection
                key="default"
                label={language?.lead?.default_value ?? "Default Value"}
            >
                <SummaryPopoverValue>{defaultValue}</SummaryPopoverValue>
            </SummaryPopoverSection>,
        );
    }
    if (hasPrefix) {
        sections.push(
            <SummaryPopoverSection
                key="prefix"
                label={language?.lead?.prefix_label ?? "Prefix Label"}
            >
                <SummaryPopoverValue>{prefix}</SummaryPopoverValue>
            </SummaryPopoverSection>,
        );
    }
    if (hasComparisons) {
        sections.push(
            <SummaryPopoverSection
                key="comparisons"
                label={language?.lead?.comparisons ?? "Comparisons"}
            >
                {comparisons.map((row, rowIndex) => (
                    <SummaryPopoverValue key={rowIndex}>
                        {`${row?.compare_to || "—"} → ${row?.map_to || "—"}`}
                    </SummaryPopoverValue>
                ))}
            </SummaryPopoverSection>,
        );
    }

    return (
        <SummaryPopover open={open} anchorEl={anchorEl} onClose={onClose}>
            {hasAnything ? (
                <>
                    <Box
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                            padding: "8px 10px 8px 14px",
                            backgroundColor: "#ffffff",
                            borderBottom: "1px solid #f2f2f4",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "10px",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: "#86868b",
                                fontFamily: '"Nunito", sans-serif',
                            }}
                        >
                            {language?.connection_detail?.options ?? "Options"}
                        </Typography>
                        <CopyOptionsJsonButton
                            getJson={getJson}
                            label={language?.lead?.copy_json ?? "Copy JSON"}
                        />
                    </Box>
                    {sections.map((section, sectionIndex) =>
                        React.cloneElement(section, {
                            isLast: sectionIndex === sections.length - 1,
                        }),
                    )}
                </>
            ) : (
                <Box sx={{ padding: "12px 14px" }}>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: "#86868b",
                            fontFamily: '"Nunito", sans-serif',
                        }}
                    >
                        {language?.lead?.no_options_configured ??
                            "No options configured yet. Click to add functions, a default value, a prefix label or comparisons."}
                    </Typography>
                </Box>
            )}
        </SummaryPopover>
    );
};

export default FieldOptionsSummaryPopover;
